import { FCC } from "@/types/common";
import { Typography, Stack, styled, Box, Tooltip } from "@mui/material";
import { TextWithLinks } from "@/components/commons/TextWithLinks";
import { EMPTY_STR } from "@/constants/string";
import Divider from "@mui/material/Divider";
import { black, red, text } from "@/theme/colors";
import { convertNumberToZipCode } from "@/utils/input";
import { IconCopy } from "@/assets/icons/IconCopy";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { truncateWithEllipsis } from "@/utils/string";

type TCaseInfoItem = {
  label?: string;
  value?: string | null | undefined;
  direction?: "row" | "column";
  type?: string;
  color?: string;
  suffix?: string;
  required?: boolean;
  endIcon?: React.ReactNode;
  isShowDivider?: boolean;
  copyable?: boolean;
  copyValue?: string | (() => string);
  truncateLength?: number | null;
  onCopy?: (value: string) => void;
};
export const CaseInfoItem: FCC<TCaseInfoItem> = ({
  label,
  value,
  direction,
  type = "string",
  color,
  suffix,
  endIcon,
  required = false,
  isShowDivider = true,
  copyable = false,
  copyValue,
  truncateLength = null,
  onCopy,
}) => {
  const [, copy] = useCopyToClipboard();

  const getValue = () => {
    if (!value) return EMPTY_STR.TEXT;
    if (type === "zipcode") {
      return convertNumberToZipCode(value);
    }

    if (truncateLength !== null) {
      return truncateWithEllipsis(value, truncateLength);
    }

    return value;
  };

  const handleCopy = () => {
    let textToCopy: string;

    if (copyValue !== undefined) {
      textToCopy = typeof copyValue === "function" ? copyValue() : copyValue;
    } else {
      textToCopy = getValue() + (suffix ?? "");
    }
    copy(textToCopy);

    if (onCopy) {
      onCopy(textToCopy);
    }
  };

  return (
    <>
      <CaseInfoItemWrapper direction={direction} gap={1}>
        {label && (
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
            <Typography variant="cap12" color={black[70]}>
              {label}
              {required && (
                <Typography component="span" variant="cap12" sx={{ color: red[700], ml: 0.5 }}>
                  *
                </Typography>
              )}
            </Typography>
            {copyable && (value || copyValue) && (
              <Tooltip title="日程情報などをコピー">
                <Box
                  onClick={handleCopy}
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    color: black[70],
                    "&:hover": {
                      opacity: 0.7,
                    },
                  }}
                >
                  <IconCopy style={{ fontSize: "16px" }} />
                </Box>
              </Tooltip>
            )}
          </Stack>
        )}
        <Stack
          direction="row"
          justifyContent="space-between"
          component={type === "textArea" ? "pre" : "div"}
          sx={{ fontFamily: "inherit", margin: 0 }}
        >
          <Typography
            variant="body14Semi"
            color={color ? color : text.primary}
            {...(type === "textArea" ? { component: "span" } : {})}
            sx={type === "textArea" ? { whiteSpace: "pre-wrap", wordBreak: "break-word" } : type === "link" ? { wordBreak: "break-all" } : undefined}
          >
            {type === "link" && value ? (
              <Typography
                component="a"
                variant="body14Semi"
                href={value}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: "primary.main", textDecoration: "underline", "&:hover": { opacity: 0.7 } }}
              >
                {getValue()}
              </Typography>
            ) : type === "textArea" && value ? (
              <TextWithLinks text={value} />
            ) : (
              getValue()
            )}
            {suffix ?? ""}
          </Typography>
          <Stack direction="row" gap={1} alignItems="center">
            {endIcon}
          </Stack>
        </Stack>
        {isShowDivider && <DividerWrapper />}
      </CaseInfoItemWrapper>
    </>
  );
};

const CaseInfoItemWrapper = styled(Stack)<{ direction?: string }>`
  ${({ direction }) => (direction === "row" ? `align-items: center;` : ``)}
`;

const DividerWrapper = styled(Divider)`
  margin: 8px 0;
  width: 100%;
`;
